import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/auth/auth-provider';
import { cn } from '@/shared/lib/cn';
import { NAV_SECTIONS, type NavItem } from './navigation';

/**
 * Desktop navigation rail.
 *
 * Sections with no visible items collapse entirely, so a cashier never sees
 * an empty "Administration" heading.
 */
export function AppSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { can, user } = useAuth();
  const { pathname } = useLocation();

  const sections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.permission || can(item.permission)),
      })).filter((section) => section.items.length > 0),
    [can],
  );

  const isActive = (item: NavItem) => pathname.startsWith(item.matchPrefix ?? item.to);

  return (
    <nav className="flex h-full flex-col gap-6 overflow-y-auto px-3 py-4" aria-label={t('nav.more')}>
      <div className="flex items-center gap-2.5 px-2">
        <img src="/icons/icon-192.png" alt="" className="size-8 rounded-lg" />
        <div className="min-w-0">
          {/*
            The company's own name (Settings → Organisation), not the product's.
            `displayName` is on the session so every role can read it; the
            registry name is the fallback for a workspace that has not chosen
            one, and `app.name` covers a session stored before the field
            existed — `session.user` is read straight from localStorage, so an
            older row is cast to `AuthUser` without it.
          */}
          <p className="truncate text-sm font-semibold">
            {user?.displayName || user?.tenantName || t('app.name')}
          </p>
          <p className="text-muted-foreground truncate text-xs">{t('app.tagline')}</p>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-5">
        {sections.map((section) => (
          <div key={section.id} className="space-y-1">
            {section.labelKey ? (
              <p className="text-muted-foreground px-2 pb-1 text-[11px] font-medium tracking-wide uppercase">
                {t(section.labelKey)}
              </p>
            ) : null}

            {section.items.map((item) => {
              const active = isActive(item);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                    active
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground',
                  )}
                >
                  <item.icon className="size-4 shrink-0" aria-hidden />
                  <span className="truncate">{t(item.labelKey)}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </div>
    </nav>
  );
}
